import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import {jwtDecode} from "jwt-decode";

// Determine API URL based on environment
const getApiUrl = () => {
  // In production (when served from agritur.dk), use the production API
  if (window.location.hostname === 'agritur.dk' || window.location.hostname === 'www.agritur.dk') {
    return 'https://api.agritur.dk';
  }
  // In development, use localhost
  return 'http://localhost:8001';
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const token = localStorage.getItem("access_token");
    const refreshToken = localStorage.getItem("refresh_token");
    
    // If we have a valid access token, use it
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 > Date.now()) {
          return { token, refreshToken, user: decoded.sub };
        }
        // Token expired, but we might have refresh token - will handle in useEffect
      } catch {
        // Invalid token - clear it, but keep refresh token for attempt
        localStorage.removeItem("access_token");
      }
    }
    
    // No valid access token - return null (will try refresh in useEffect if refresh token exists)
    return null;
  });

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const fetchUser = async () => {
    try {
      if (auth?.token) {
        const response = await axios.get(`${getApiUrl()}/me`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        setUser(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      logout();
    } finally {
      setLoading(false); // Ensure loading is set to false regardless of success/failure
    }
  };

  const login = async (username, password) => {
    try {
      const params = new URLSearchParams();
      params.append("username", username);
      params.append("password", password);

      const response = await axios.post(`${getApiUrl()}/token`, params, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      const { access_token, refresh_token } = response.data;
      const decoded = jwtDecode(access_token);

      // Update auth state and save tokens
      setAuth({ token: access_token, refreshToken: refresh_token, user: decoded.sub });
      localStorage.setItem("access_token", access_token);
      if (refresh_token) {
        localStorage.setItem("refresh_token", refresh_token);
      }

      // Fetch user details
      await fetchUser();
    } catch (err) {
      console.error("Login error:", err.response?.data?.detail || err.message);
      throw err;
    }
  };

  const register = async (username, email, password, invitationCode) => {
    try {
      const response = await axios.post(`${getApiUrl()}/register`, {
        username,
        email,
        password,
        invitation_code: invitationCode,
      });

      return response.data;
    } catch (err) {
      console.error("Registration error:", err.response?.data?.detail || err.message);
      throw err;
    }
  };

  const logout = () => {
    setAuth(null);
    setUser(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  };

  const refreshToken = async () => {
    const storedRefreshToken = localStorage.getItem("refresh_token") || auth?.refreshToken;
    
    if (!storedRefreshToken) {
      console.error("No refresh token available");
      logout();
      return false;
    }

    try {
      // Check if refresh token is expired
      try {
        const decoded = jwtDecode(storedRefreshToken);
        if (decoded.exp * 1000 <= Date.now()) {
          console.error("Refresh token has expired");
          logout();
          return false;
        }
      } catch (decodeErr) {
        console.error("Invalid refresh token format:", decodeErr);
        logout();
        return false;
      }

      const response = await axios.post(`${getApiUrl()}/refresh-token`, {
        refresh_token: storedRefreshToken
      });
      
      const { access_token } = response.data;
      const decoded = jwtDecode(access_token);
      
      setAuth({ token: access_token, refreshToken: storedRefreshToken, user: decoded.sub });
      localStorage.setItem("access_token", access_token);
      return true;
    } catch (err) {
      console.error("Token refresh failed:", err);
      logout();
      return false;
    }
  };

  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token);
      return decoded.exp * 1000 <= Date.now();
    } catch {
      return true;
    }
  };

  // Initialize auth state and attempt refresh on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const storedAccessToken = localStorage.getItem("access_token");
      const storedRefreshToken = localStorage.getItem("refresh_token");
      
      // If we have a valid access token, set auth and fetch user
      if (storedAccessToken && !isTokenExpired(storedAccessToken)) {
        try {
          const decoded = jwtDecode(storedAccessToken);
          setAuth({ token: storedAccessToken, refreshToken: storedRefreshToken, user: decoded.sub });
          // Will fetch user in next useEffect
          return;
        } catch {
          // Invalid token, continue to refresh attempt
        }
      }
      
      // Access token is missing or expired - try to refresh
      if (storedRefreshToken) {
        // Check if refresh token is expired
        if (isTokenExpired(storedRefreshToken)) {
          // Refresh token expired, clear everything
          logout();
          setLoading(false);
          return;
        }
        
        // Try to refresh access token
        const success = await refreshToken();
        if (success) {
          // Refresh succeeded, will fetch user in next useEffect
          return;
        } else {
          setLoading(false);
          return;
        }
      }
      
      // No tokens available
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Fetch user when auth is available
  useEffect(() => {
    if (auth && auth.token && !isTokenExpired(auth.token)) {
      fetchUser();
    }
  }, [auth]);

  // Proactive token refresh
  useEffect(() => {
    if (!auth?.token) return;
    
    const checkTokenExpiration = async () => {
      try {
        const decoded = jwtDecode(auth.token);
        const expirationTime = decoded.exp * 1000;
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;
        
        // Refresh if less than 5 minutes remaining
        if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
          await refreshToken();
        }
      } catch (err) {
        console.error("Error checking token expiration:", err);
      }
    };
    
    // Check immediately and then every minute
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [auth?.token]);

  return (
    <AuthContext.Provider value={{ auth, user, loading, login, register, logout, setUser, refreshToken }}>
      {!loading && children} {/* Render children only after loading is complete */}
    </AuthContext.Provider>
  );
};

export default AuthContext;
